import React, { useState, useEffect } from 'react';
import { helpService } from '../services/helpService';
import { atlasService } from '../services/atlasService';
import { FiX, FiSearch, FiMessageSquare, FiBookOpen, FiChevronLeft } from 'react-icons/fi';
import { renderMarkdown } from '../utils/markdownUtils';
import { WaitingIndicator } from '@trip-planner-monorepo/shared';
import './AssistanceDock.css';

const AssistanceDock = () => {
    const [helpState, setHelpState] = useState(helpService.getState());
    const [activeTab, setActiveTab] = useState('guide'); // 'guide' or 'chat'
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState('context'); // 'context' or 'search' or 'index'

    const guides = atlasService.getGuides();
    const searchResults = searchQuery ? atlasService.search(searchQuery) : [];

    const [isResizing, setIsResizing] = useState(false);

    useEffect(() => {
        const unsubscribe = helpService.subscribe((newState) => {
            setHelpState(newState);
        });
        return unsubscribe;
    }, []);

    const startResizing = (e) => {
        setIsResizing(true);
        e.preventDefault();
    };

    const stopResizing = () => {
        setIsResizing(false);
    };

    const resize = (e) => {
        if (isResizing) {
            const newWidth = window.innerWidth - e.clientX;
            if (newWidth > 300 && newWidth < 800) {
                helpService.setDockWidth(newWidth);
            }
        }
    };

    useEffect(() => {
        if (isResizing) {
            window.addEventListener('mousemove', resize);
            window.addEventListener('mouseup', stopResizing);
        } else {
            window.removeEventListener('mousemove', resize);
            window.removeEventListener('mouseup', stopResizing);
        }
        return () => {
            window.removeEventListener('mousemove', resize);
            window.removeEventListener('mouseup', stopResizing);
        };
    }, [isResizing]);

    if (!helpState.isOpen) {
        return <div className="assistance-dock" style={{ display: 'none' }} />;
    }

    const handleClose = () => {
        helpService.closeHelp();
    };

    const handleGuideSelect = (guideId) => {
        helpService.openHelp(guideId);
        setViewMode('context');
        setSearchQuery('');
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            setViewMode('search');
        }
    };

    return (
        <div
            className={`assistance-dock ${helpState.isOpen ? 'is-open' : ''}`}
            style={{ width: `${helpState.dockWidth}px` }}
        >
            <div className="dock-resizer" onMouseDown={startResizing} />
            <div className="dock-header">
                <h2>itineri Assistant</h2>
                <button className="dock-close-btn" onClick={handleClose} title="Close Assistant">
                    <FiX />
                </button>
            </div>

            <div className="dock-tabs">
                <button
                    className={`dock-tab ${activeTab === 'guide' ? 'active' : ''}`}
                    onClick={() => setActiveTab('guide')}
                >
                    <FiBookOpen style={{ marginRight: '6px' }} />
                    Guide
                </button>
                <button
                    className={`dock-tab ${activeTab === 'chat' ? 'active' : ''}`}
                    onClick={() => setActiveTab('chat')}
                >
                    <FiMessageSquare style={{ marginRight: '6px' }} />
                    AI Chat
                </button>
            </div>

            <div className="dock-content">
                {activeTab === 'guide' ? (
                    <>
                        <div className="dock-search-container">
                            <FiSearch color="#888" />
                            <input
                                type="text"
                                placeholder="Search guides..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={handleSearchKeyPress}
                            />
                        </div>

                        <div className="dock-main-inner" style={{ padding: '16px' }}>
                            {(viewMode === 'search' || (searchQuery && viewMode !== 'context')) ? (
                                <div className="search-results">
                                    <div className="back-link" onClick={() => setViewMode('context')}>
                                        <FiChevronLeft /> Back to current guide
                                    </div>
                                    <h3>Search Results</h3>
                                    <ul className="guide-list">
                                        {searchResults.length > 0 ? (
                                            searchResults.map(guide => (
                                                <li key={guide.id} className="guide-item" onClick={() => handleGuideSelect(guide.id)}>
                                                    <h3>{guide.title}</h3>
                                                    <p>{guide.description}</p>
                                                </li>
                                            ))
                                        ) : (
                                            <p>No guides found for "{searchQuery}"</p>
                                        )}
                                    </ul>
                                    <div style={{ marginTop: '20px' }}>
                                        <button className="back-link" onClick={() => setViewMode('index')}>
                                            Browse all guides
                                        </button>
                                    </div>
                                </div>
                            ) : viewMode === 'index' ? (
                                <div className="guide-index">
                                    <div className="back-link" onClick={() => setViewMode('context')}>
                                        <FiChevronLeft /> Back to current guide
                                    </div>
                                    <h3>All Guides</h3>
                                    <ul className="guide-list">
                                        {guides.map(guide => (
                                            <li key={guide.id} className="guide-item" onClick={() => handleGuideSelect(guide.id)}>
                                                <h3>{guide.title}</h3>
                                                <p>{guide.description}</p>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ) : (
                                <div className="assistance-content">
                                    <div className="back-link" onClick={() => setViewMode('index')}>
                                        Browse all guides
                                    </div>
                                    {helpState.isLoading ? (
                                        <div style={{ padding: '40px 0' }}>
                                            <WaitingIndicator size="small" message="Loading guide..." />
                                        </div>
                                    ) : helpState.error ? (
                                        <p className="error">{helpState.error}</p>
                                    ) : (
                                        <div className="markdown-body">
                                            {renderMarkdown(helpState.content || 'No guide selected', '/docs/')}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="ai-chat-placeholder">
                        <p>AI Assistance coming soon!</p>
                        <p>I will be able to answer questions about your trip and help you navigate itineri.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AssistanceDock;
